import { useState, useEffect, useCallback } from "react";
import { Plus, DoorOpen, UserPlus, Briefcase, ChevronDown, ChevronUp, Users } from "lucide-react";
import api from "../api";
import { Button, Input, Select } from "./ui";

/**
 * Per-event Workspaces tab (mirrors the mobile event Workspaces tab):
 * create workspaces tied to this event, add rooms, add vendors from the
 * accepted-bid list, and add staff by email with a role that grants rights
 * (Manager can scan check-ins).
 */
export default function EventWorkspacesTab({ event }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({});
  const [open, setOpen] = useState(null);
  const [hired, setHired] = useState([]);
  const [msg, setMsg] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [roomFor, setRoomFor] = useState(null);
  const [room, setRoom] = useState({ name: "", purpose: "" });
  const [vendorFor, setVendorFor] = useState(null);
  const [staffFor, setStaffFor] = useState(null);
  const [staff, setStaff] = useState({ email: "", role: "member" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/communities/workspaces/`);
      setWorkspaces((res.data || []).filter((w) => w.event === event.id));
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/bids/events/${event.slug}/hired-providers/`).then((r) => setHired(r.data || [])).catch(() => {});
  }, [event.slug]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  const toggle = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    if (!details[id]) {
      try { const res = await api.get(`/communities/workspaces/${id}/`); setDetails((d) => ({ ...d, [id]: res.data })); } catch (e) {}
    }
  };
  const refreshDetail = async (id) => {
    try { const res = await api.get(`/communities/workspaces/${id}/`); setDetails((d) => ({ ...d, [id]: res.data })); } catch (e) {}
  };

  const createWorkspace = async () => {
    if (!name.trim()) return;
    try { await api.post(`/communities/workspaces/`, { name: name.trim(), event: event.id }); setName(""); setShowCreate(false); flash("Workspace created"); load(); }
    catch (e) { flash("Couldn't create workspace"); }
  };
  const addRoom = async (wsId) => {
    if (!room.name.trim()) return;
    try { await api.post(`/communities/workspaces/${wsId}/rooms/`, room); setRoom({ name: "", purpose: "" }); setRoomFor(null); flash("Room added"); refreshDetail(wsId); load(); }
    catch (e) { flash("Couldn't add room"); }
  };
  const addVendor = async (wsId, userId) => {
    try { await api.post(`/communities/workspaces/${wsId}/members/`, { user_id: userId, role: "vendor" }); setVendorFor(null); flash("Vendor added"); refreshDetail(wsId); load(); }
    catch (e) { flash("Couldn't add vendor"); }
  };
  const addStaff = async (wsId) => {
    if (!staff.email.includes("@")) return;
    try { await api.post(`/communities/workspaces/${wsId}/invite/`, staff); setStaff({ email: "", role: "member" }); setStaffFor(null); flash("Added / invited"); refreshDetail(wsId); load(); }
    catch (e) { flash("Couldn't add staff"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Workspaces</h2>
        </div>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setShowCreate((s) => !s)}>New workspace</Button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Organise your event team into rooms (Vendors, Security, Staff…). Add vendors from your accepted bids,
        and add staff by email — give a Manager the right to scan check-ins.
      </p>

      {msg && <div className="text-sm text-green-600 dark:text-green-400">{msg}</div>}

      {showCreate && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex gap-2 items-end bg-white dark:bg-gray-800">
          <div className="flex-1"><Input label="Workspace name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vendors" /></div>
          <Button onClick={createWorkspace}>Create</Button>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 py-6 text-center">Loading…</p>
      ) : workspaces.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-gray-500">
          No workspaces for this event yet. Create one to start.
        </div>
      ) : (
        <div className="space-y-3">
          {workspaces.map((ws) => {
            const d = details[ws.id];
            const isOpen = open === ws.id;
            return (
              <div key={ws.id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <button onClick={() => toggle(ws.id)} className="w-full flex items-center justify-between p-4">
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 dark:text-white">{ws.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(ws.metrics?.members ?? 0)} members · {(ws.metrics?.rooms ?? 0)} rooms
                    </p>
                  </div>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" leftIcon={<DoorOpen size={15} />} onClick={() => setRoomFor(roomFor === ws.id ? null : ws.id)}>Add room</Button>
                      <Button size="sm" variant="outline" leftIcon={<Briefcase size={15} />} onClick={() => setVendorFor(vendorFor === ws.id ? null : ws.id)}>Add vendor</Button>
                      <Button size="sm" variant="outline" leftIcon={<UserPlus size={15} />} onClick={() => setStaffFor(staffFor === ws.id ? null : ws.id)}>Add staff</Button>
                    </div>

                    {roomFor === ws.id && (
                      <div className="flex flex-wrap gap-2 items-end rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
                        <div className="flex-1 min-w-[8rem]"><Input label="Room name" value={room.name} onChange={(e) => setRoom({ ...room, name: e.target.value })} placeholder="Catering" /></div>
                        <div className="flex-1 min-w-[8rem]"><Input label="Purpose" value={room.purpose} onChange={(e) => setRoom({ ...room, purpose: e.target.value })} placeholder="optional" /></div>
                        <Button size="sm" onClick={() => addRoom(ws.id)}>Add</Button>
                      </div>
                    )}

                    {vendorFor === ws.id && (
                      <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Only providers whose bids you accepted:</p>
                        {hired.length === 0 ? (
                          <p className="text-sm text-gray-500">No hired providers yet — accept a service bid first.</p>
                        ) : (
                          <div className="space-y-1">
                            {hired.map((p) => (
                              <button key={p.id} onClick={() => addVendor(ws.id, p.id)}
                                className="w-full text-left px-3 py-2 rounded-md hover:bg-white dark:hover:bg-gray-700 flex items-center justify-between">
                                <span className="text-sm text-gray-800 dark:text-gray-200">{p.name} {p.service ? <span className="text-gray-400">· {p.service}</span> : null}</span>
                                <Plus size={15} className="text-blue-600" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {staffFor === ws.id && (
                      <div className="flex flex-wrap gap-2 items-end rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
                        <div className="flex-1 min-w-[10rem]"><Input label="Email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} placeholder="name@email.com" /></div>
                        <div className="min-w-[8rem]">
                          <Select label="Role" value={staff.role} onChange={(e) => setStaff({ ...staff, role: e.target.value })}>
                            <option value="manager">Manager (can scan)</option>
                            <option value="member">Member</option>
                            <option value="participant">Participant</option>
                          </Select>
                        </div>
                        <Button size="sm" onClick={() => addStaff(ws.id)}>Add</Button>
                      </div>
                    )}

                    {/* Members */}
                    {d?.members?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Members</p>
                        <ul className="text-sm text-gray-700 dark:text-gray-200 space-y-1">
                          {d.members.map((m) => (
                            <li key={m.id} className="flex justify-between">
                              <span>{m.name || m.email}</span>
                              <span className="text-gray-400">{(m.role || "").replace(/^\w/, (c) => c.toUpperCase())}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Rooms */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Rooms</p>
                      {(!d?.rooms || d.rooms.length === 0) ? (
                        <p className="text-sm text-gray-500">No rooms yet.</p>
                      ) : (
                        <ul className="text-sm text-gray-700 dark:text-gray-200 space-y-1">
                          {d.rooms.map((r) => (
                            <li key={r.id} className="flex items-center gap-2"><DoorOpen size={14} className="text-gray-400" /> {r.name}{r.description ? <span className="text-gray-400">— {r.description}</span> : null}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
